import React, { useEffect, useState } from 'react';
import { Col, Row, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {
  Slider,
  CircularProgress,
  createTheme,
  ThemeProvider,
  Button,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { Info as InfoIcon } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import DigitalCoverageAmounts from '../../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts';
import ProductCoverageLength from '../../../Helpers/Lists/CoverageLengths/ProductCoverageLength';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import TobaccoUsage from '../../Common/Helpers/TobaccoUsage';
import ShieldIcon from '../../../../icons/shield.svg?react';
import TimeIcon from '../../../../icons/time.svg?react';
import DollarIMacIcon from '../../../../icons/dollar-imac.svg?react';
import { SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_TERM_STRUCTURE, getSessionStorageItem, getBasicInfo, setBasicInfo, setSessionStorageItem } from '../../../../sessionStorage';
import Age from '../../../Helpers/Dates/Age';
import { CheckboxInput } from "../../../Helpers/Inputs";
import IsMultiTerm from "../../Common/Helpers/IsMultiTerm";
import NeedsAnalysisButton from '../../../Tools/NeedsAnalysis/NeedsAnalysisButton';
import QuoteBasicInfo from '../../../Quoters/Common/QuoteBasicInfo';
import GetProductTypeEnum from '../../../../types/productType';
import CustomerSupportPhone from '../../../Account/CustomerSupportPhone';
import CustomerSupportEmail from '../../../Account/CustomerSupportEmail';
import './PreQuote.scss';

const quoterTheme = {
  typography: {
    fontFamily: "museo-sans",
    body1: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          "&.coverage-amount": {
            display: "flex",
            alignItems: "center",
          },
          "&.coverage-amount .coverage-input": {
            display: "flex",
            width: 188,
            height: 47,
            alignItems: "center",
            borderRadius: 6,

          },
          "&.coverage-amount .coverage-input::before": {
            content: "none",
          },
          "&.coverage-amount .coverage-input::after": {
            content: "none",
          },
          "&.coverage-amount .coverage-input input": {
            padding: 0,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: 16,
        },
        root: {
          height: 56,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        underline: {
          "&::before": {
            borderBottom: "solid 2px #b2b2b2",
          },
          "&:hover::before": {
            borderBottomWidth: "2px",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 16,
          color: "rgba(0,0,0,0.6)",
        }
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 0,
          paddingTop: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          color: "rgba(0,0,0,0.3) !important",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        fontSize: 16,
        icon: {
          color: "rgba(0,0,0,1)"
        },
        iconFilled: {
          right: 16,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 4,
          margin: "8px 0",
          padding: 0,
        },
        rail: {
          height: 4,
          borderRadius: 2,
          backgroundColor: " #ccc",
          opacity: 1,
        },
        track: {
          height: 4,
          borderRadius: 2,
        },
        mark: {
          display: "none",
        },
        thumb: {
          marginTop: 0,
          height: '12px !important',
          width: '12px !important',
          '&::before': {
            boxShadow: 'none !important',
          },
        },
        markLabel: {
          top: 9,
          fontSize: 12,
          color: "#333",
          "&[style*='0%;']": {
            "left": "0px !important",
            "transform": "translateX(0)",
          },
          "&[style*='100%;']": {
            "right": "0px !important",
            "left": "auto !important",
            "transform": "translateX(0)",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: 24,
          height: 24,
          marginBottom: 1,
        },
        colorAction: {
          fill: "#b2b2b2",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: "none",
          boxShadow: "0 2px 3px rgba(0, 0, 0, 0.161)",
        },
      },
    },
  },
};

export default function PreQuote(props) {
  const basicInfoDetails = getBasicInfo();
  const [loading, setLoading] = useState(true);
  const [quoteResults, setQuoteResults] = useState([]);
  const [multiTermResult, setMultiTermResult] = useState(false);
  const [coverageAmount, setCoverageAmount] = useState(basicInfoDetails.faceAmount || parseInt(props.formState.coverageAmountQuote, 10));
  const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;
  var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

  useEffect(() => {
    props.onMultiTerm(multiTermResult);
    setSessionStorageItem(SESSION_STORAGE_TERM_STRUCTURE, { multiTerm: multiTermResult })
  }, [multiTermResult])

  const runQuote = (getIsSubscribed) => {

    const feet = props.basicInfo.height == null || props.basicInfo.height === '' ? 0 : props.basicInfo.height.split('ft')[0].trim();
    const inches = props.basicInfo.height == null || props.basicInfo.height === '' ? 0 : props.basicInfo.height.split('ft')[1].split('in')[0].trim();
    const weight = props.basicInfo.weight == null || props.basicInfo.weight === '' ? 0 : props.basicInfo.weight.split(' ')[0];
    const tobacco = TobaccoUsage(props.basicInfo.tobacco);

    fetch(`${window.__ENV__.REACT_APP_COVR_SERVICES_URL}/api/VSS/GetCustomHealthClass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ aboutMe: props.basicInfo }),
    })
      .then(res => res.json())
      .then(customHealthClassResponse => {
        const queryParams = {
          birthdate: props.basicInfo.birthdate,
          state: props.basicInfo.state,
          gender: props.basicInfo.gender,
          faceAmount: coverageAmount,
          tobacco: tobacco,
          healthClass: customHealthClassResponse.defaultHealthClass,
          term: props.formState.termQuote,
          heightFt: feet,
          heightIn: inches,
          weight: weight,
          oneClickAccountId: accountObj.account.oneClickAccountId,
          replacePolicy: props.formState.replacePolicy,
          noKnockouts: customHealthClassResponse.isQualifiedForDigital,
          sessionId: accountObj.sessionId,
          isLevelTerm: true,
          isDecreasingTerm: true,
        };

        fetch(`${covrServicesUrl}/api/MCT/GetMCTQuote`, {
          method: 'POST',
          headers: {
            'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(queryParams),
        })
          .then((res) => res.json())
          .then((data) => {
            if (getIsSubscribed()) {
              var quoteResults = data && data.quoteResults;
              if (quoteResults && quoteResults.length) {

                setMultiTermResult(IsMultiTerm(data));
                setQuoteResults(quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0]);

                // save off QuoteLogId here.............

              } else {
                setQuoteResults([]);
              }
              setLoading(false);
            }
          })
          .catch((error) => {
            if (getIsSubscribed()) {
              ErrorHandler(error, setQuoteResults);
              setLoading(false);
            }
          });
      });
  };

  let debounce = null;
  useEffect(() => {
    if (coverageAmount == null || props.formState.termQuote == null) {
      return;
    }

    setLoading(true);

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };

  }, [coverageAmount, props.formState.termQuote]);

  const onSubmit = () => {
    const basicInfo = getBasicInfo();
    basicInfo.faceAmount = coverageAmount
    basicInfo.termLength = props.formState.termQuote;
    setBasicInfo(basicInfo);
    var form = { ...props.fullFormState };
    form.pages.preQuote.coverageAmountQuote = coverageAmount
    props.onMultiFieldChange(form);
    props.onNext()
  }
  const [preQuoteTooltip, setPreQuoteTooltip] = useState(false);
  const handleTooltipPreQuoteClose = () => {
    setPreQuoteTooltip(false);
  };
  const handleTooltipPreQuoteOpen = () => {
    setPreQuoteTooltip(true);
  };

  useEffect(() => {
    // dynamic term length based on age

    const age = Age(props.basicInfo.birthdate);
    if (age <= 45) {
      props.onFieldChange('termQuote', 30);
    } else if (age <= 55) {
      props.onFieldChange('termQuote', 20);
    } else {
      props.onFieldChange('termQuote', 10);
    }
  }, [])

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...quoterTheme,
    })}>
      <Container className="p-0 outer-div" >
        <div className="MCT-info d-flex justify-content-center inner-div" >
          <div className="pre-quote-form">
            <Row className="pre-quote-container">
              <Col lg={8} xs={12} sm={12} md={12}>
                <div className="bg-white block">
                  <Row className="text-center">
                    <Col>
                      <div className="d-flex justify-content-center">
                        <div className="icon-container background-color-16">
                          <SvgIcon component={ShieldIcon} color='primary' />
                        </div>
                      </div>
                      <div className="card-title">Select Coverage Amount</div>
                      <div className='amount-div'>
                        <div className='text-decor pt-0 pb-0'>
                          <NumericFormat
                            className="quote-amount-dollars"
                            value={coverageAmount}
                            displayType={'text'}
                            thousandSeparator={true}
                            decimalScale={0}
                            fixedDecimalScale={true}
                          />
                        </div>
                      </div>

                      <div className="coverage-slider">
                        <Slider
                          min={100000}
                          max={3000000}
                          step={50000}
                          value={Number(coverageAmount)}
                          onChange={(e, v) => { props.onFieldChange("coverageAmountQuote", v); setCoverageAmount(v) }}
                          className="coverage-amount"
                          marks={DigitalCoverageAmounts()}
                          role="slider"
                          aria-labelledby="coverageAmountQuote"
                        />
                      </div>

                      <p className="tag-line desc-background">
                        Most financial experts recommend 10-15x your income plus your mortgage balance.
                      </p>
                      <div className="pt-2">
                        <NeedsAnalysisButton
                          isPrequote={true}
                          title="HOW MUCH I NEED"
                          setCoverageAmount={(face) => { props.onFieldChange("coverageAmountQuote", face); setCoverageAmount(face) }}
                          type="quote-screen"
                        />
                      </div>
                    </Col>
                    <Col>
                      <div className="d-flex justify-content-center">
                        <div className="icon-container background-color-16">
                          <SvgIcon component={TimeIcon} color='primary' />
                        </div>
                      </div>
                      <div className="card-title">Years of Coverage</div>
                      <div className="term-slider">
                        <div className='text-decor pt-0 pb-0'

                        ><span>{props.formState.termQuote}</span>
                          <span className='ms-1 inner-span'>Years</span>
                        </div>
                      </div>

                      <div className="coverage-slider">
                        <Slider
                          min={10}
                          max={30}
                          step={5}
                          value={Number(props.formState.termQuote)}
                          onChange={(e, v) => props.onFieldChange("termQuote", v)}
                          className="coverage-amount"
                          marks={ProductCoverageLength()}
                          role="slider"
                          aria-labelledby="termQuote"
                        />
                      </div>
                      <p className="tag-line desc-background">Choose a term length that will cover your longest financial commitment, such as a mortgage or loan.</p>
                    </Col>
                  </Row>

                </div>
              </Col>
              {loading ?
                <Col lg={4} xs={12} sm={12} md={12}>
                  <div className="loader-container">
                    <CircularProgress />
                  </div>
                </Col>
                :
                <Col lg={4} xs={12} sm={12} md={12} className="cursor-pointer">
                  <div className="bg-white block quote-rate">
                    <Row className="text-center">
                      <Col>
                        <div className="d-flex justify-content-center">
                          <div className="icon-container background-color-16">
                            <SvgIcon component={DollarIMacIcon} color='primary' />
                          </div>
                        </div>

                        {quoteResults.monthlyRate !== undefined && (
                          <React.Fragment>
                            <div className="d-flex justify-content-center quote-amount-title">
                              <p className="card-title mb-0">Rates as low as
                                <Tooltip
                                  title="You’re unique. Based on your information, such as height, weight, and nicotine usage, this estimates how little you could pay per month with the best health rating. This rate is subject to change based on the results of your application process. To get a more accurate quote, candidly answer the following questions."
                                  className="toolInfo"
                                  role="tooltip"
                                  id="preQuote-tooltip"
                                  aria-describedby="preQuote-tooltip"
                                  aria-label="more about sort by price"
                                  enterTouchDelay={0}
                                  leaveTouchDelay={5000}
                                  onClose={() => handleTooltipPreQuoteClose()}
                                  open={preQuoteTooltip}
                                  placement="top"
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      className: 'light-tooltip',
                                      sx: { fontSize: '12px', maxWidth: 385, boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12) !important' }
                                    },
                                    arrow: {
                                      className: 'custom-light-tooltip-arrow',
                                    },
                                  }}
                                >
                                  <button type='button' id="preQuote" role="tooltip" className="quote-tooltip" onMouseEnter={() => handleTooltipPreQuoteOpen()} onClick={() => handleTooltipPreQuoteOpen()} tabIndex="0" aria-disabled="true" aria-describedby="preQuote"><InfoIcon color="action"></InfoIcon></button>
                                </Tooltip>
                              </p>
                            </div>
                            <div className="quote-amount-dollars-container">
                              <NumericFormat
                                className="quote-amount-dollars-inner primary-color"
                                value={quoteResults.monthlyRate || 'No quotes available'}
                                displayType={'text'}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                              <span className="quote-amount-mo primary-color"> {quoteResults.monthlyRate !== undefined ? '/mo' : ''}</span>
                            </div>
                          </React.Fragment>
                        )}
                      </Col>
                    </Row>

                    {quoteResults.monthlyRate === undefined ? (
                      <div className="mt-3 desc-background">
                        <p className='tag-line phone-email-text'>We’d like to talk to you about your coverage requirement to make sure the product solutions will meet all of your needs. Contact one of our licensed experts to discuss your options.</p>
                        <p className='tag-line'>Email us at <a href={`mailto:${CustomerSupportEmail()}`}>{CustomerSupportEmail()}</a></p>
                        <p className='tag-line'>Please call us at <a href={`tel:${CustomerSupportPhone()}`}>{CustomerSupportPhone()}</a></p>
                      </div>
                    ) : (
                      <React.Fragment>
                        <div className="no-content"></div>
                        <p className="tag-line desc-background">
                          Answer a few more questions to see the policy options that best fit your needs.
                        </p>
                      </React.Fragment>
                    )}
                    <div className="no-content"></div>
                  </div>
                </Col>
              }
            </Row>
            <Row className='justify-content-center replace-policy-container'>
              <div className='mb-3 px-0'>
                <CheckboxInput
                  name="replacePolicy"
                  id="replacePolicy"
                  checked={props.formState.replacePolicy}
                  onChange={e => props.onFieldChange('replacePolicy', e.target.checked)}
                  label="This coverage will replace an existing policy"
                />
              </div>
            </Row>

            <Row className="pre-quote-nav-control">
              <Col className="text-start btn-back">
                <Button variant="outlined" color="primary" className="back-button" size="medium" onClick={props.onBack}>BACK</Button>
              </Col>
              <Col className="text-end">
                <Button variant="contained" disabled={loading || quoteResults.monthlyRate === undefined} size="medium" onClick={onSubmit} color="primary" >NEXT</Button>
              </Col>
            </Row>
          </div>
        </div>
        <QuoteBasicInfo {...props} />
      </Container>
    </ThemeProvider>
  );
}

PreQuote.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onMultiTerm: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

PreQuote.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    coverageAmountQuote: prefill ? 500000 : (basicInfo && basicInfo.faceAmount) || 500000,
    termQuote: prefill ? '20' : (basicInfo && basicInfo.termLength) || '20',
    replacePolicy: prefill ? true : false
  }
};
